// src/components/agentStatus/commandDisplays/ResourceDisplay.jsx

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip } from '@mui/material';
import ResourceTypeIcon from '../../resources/ResourceTypeIcon';
import { useUI } from '../../../context/UIContext';
import { useResources } from '../../../context/ResourceContext';

const RESOURCE_TAG_REGEX = /\[RESOURCE\](.*?)\[\/RESOURCE\]/;

const ResourceDisplay = ({ command }) => {
  const { openResourceTab, setActiveResourceTab, openResources } = useUI();
  const { getResourceById } = useResources();

  const resourceDetails = useMemo(() => {
    let id = null;
    let type = 'unknown';
    let title = 'Resource';
    let sourceData = null;

    if (command.resourceInfo && command.resourceInfo.id) {
      id = command.resourceInfo.id;
      type = command.resourceInfo.resource_type || 'unknown';
      title = command.resourceInfo.title || command.resourceInfo.name || id;
      sourceData = command.resourceInfo;
      console.debug("ResourceDisplay: Details from command.resourceInfo");
    }
    else if (typeof command.content === 'string') {
      const match = command.content.match(RESOURCE_TAG_REGEX);
      if (match && match[1]) {
        id = match[1].trim();
        const resourceFromContext = getResourceById(id);
        if (resourceFromContext) {
          type = resourceFromContext.type || 'unknown';
          title = resourceFromContext.title || id;
          sourceData = resourceFromContext;
           console.debug("ResourceDisplay: Details parsed from content tag and found in context");
        } else {
          type = command.type === 'resource' ? 'unknown' : command.type || 'unknown';
          title = id;
          sourceData = { id, type, title };
          console.debug("ResourceDisplay: Details parsed from content tag, NOT found in context yet");
        }
      }
    }
    else if (command.type === 'resource' || command.msg_type === 'RESOURCE') {
        id = command.id || null;
        type = command.resource_type || 'unknown';
        title = command.title || command.name || id || 'Resource';
        if (id) {
            sourceData = command;
            console.debug("ResourceDisplay: Details from command object properties");
        } else {
             console.warn("ResourceDisplay: 'resource' type message lacks ID.", command);
        }
    }
    return { id, type, title, sourceData };
  }, [command, getResourceById]);

  const { id: resourceId, type: resourceType, title: resourceTitle, sourceData } = resourceDetails;
  const isSignal = !!command.resourceInfo || (typeof command.content === 'string' && command.content.includes('[RESOURCE]'));
  const displayLabel = `Resource: ${resourceTitle}`;

  const handleClick = useCallback(() => {
    const isClickable = !!(resourceId && resourceType && resourceType !== 'unknown');
    if (!isClickable) {
        console.warn("Cannot open resource: ID or Type is missing or unknown.", { resourceId, resourceType, command });
        return;
    }
    const alreadyOpen = openResources.some(res => res.id === resourceId);
    if (alreadyOpen) {
      console.log(`Resource ${resourceId} already open, switching tab.`);
      setActiveResourceTab(resourceId);
    } else {
      console.log(`Resource ${resourceId} not open, opening new tab.`);
      const resourceDataToOpen = sourceData && sourceData.id === resourceId
        ? sourceData
        : { id: resourceId, type: resourceType, title: resourceTitle };
      if (!resourceDataToOpen.resource_type && resourceDataToOpen.type) {
          resourceDataToOpen.resource_type = resourceDataToOpen.type;
      }
      openResourceTab(resourceDataToOpen);
    }
  }, [
      resourceId,
      resourceType,
      resourceTitle,
      sourceData,
      openResources,
      openResourceTab,
      setActiveResourceTab,
      command
  ]);

  const isClickable = !!(resourceId && resourceType && resourceType !== 'unknown');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ResourceTypeIcon type={resourceType} />
      <Chip
        label={displayLabel}
        size="small"
        variant="outlined"
        color={isSignal ? "info" : "default"}
        onClick={isClickable ? handleClick : undefined}
        sx={{
          maxWidth: '100%',
          cursor: isClickable ? 'pointer' : 'default',
          '&:hover': isClickable ? {
            backgroundColor: 'action.hover',
          } : {},
        }}
      />
    </Box>
  );
};

ResourceDisplay.propTypes = {
  command: PropTypes.shape({
    content: PropTypes.any,
    resourceInfo: PropTypes.object,
    id: PropTypes.string,
    type: PropTypes.string,
    msg_type: PropTypes.string,
    resource_type: PropTypes.string,
    title: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

export default React.memo(ResourceDisplay);